import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Heart, X, Trash2, Package, Loader2, MapPin, Clock, ExternalLink, MoreHorizontal } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  getAllFavorites,
  clearAllFavorites,
  globalFavoritesQueryKey,
  type GlobalFavorite,
} from "../services/GlobalFavoritesService";
import { removeFavoriteWithRetailer, updateFavoriteSnapshot } from "../services/FavoritesService";
import {
  searchProduct,
  searchHomeDepotProduct,
  type StoreProductResult,
  type HomeDepotStoreProductResult,
} from "../services/ProductSearchService";
import { ONLINE_ONLY_RETAILERS as ONLINE_ONLY_RETAILER_IDS, type RetailerType } from "../config/retailers";
import { getSearchParams, shouldFallbackToSku } from "./buttons/CheckStockButton.utils";
import { getProductImageUrl } from "../utils/imageUtils";
import { resolveStoreDisplayLabel } from "../utils/storeDisplayLabel";
import { storesLoader, type RetailerWithStores } from "../utils/storesLoader";
import { useUserContext } from "../context/UserContext";
import NearbyResults from "./NearbyResults";
import ZipCodeModal from "./ZipCodeModal";
import { isFreeTier } from "../utils/userUtils";
import {
  getGlobalFavoritesExperience,
  resolveFavoriteRetailerUrl,
} from "./GlobalFavoritesButton.logic";
import type { StoreData } from "../hooks/useStores";

interface GlobalFavoritesButtonProps {
  iconSize?: number;
  variant?: "light" | "dark" | "sidebar";
}

// After this long, the saved price is probably no longer accurate.
const PRICE_OUTDATED_AFTER_MS = 48 * 60 * 60 * 1000;

const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);

const formatRelativeTime = (timestamp: number): string => {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const RETAILER_LABELS: Record<string, string> = {
  walmart: "Walmart",
  walmart_online: "Walmart.com",
  home_depot: "Home Depot",
  dollar_general: "Dollar General",
  target: "Target",
  bestbuy: "Best Buy",
  costco: "Costco",
  lowes: "Lowe's",
  tractor_supply: "Tractor Supply",
  northern_tool: "Northern Tool",
  samsclub: "Sam's Club",
  ace_hardware: "Ace Hardware",
  amazon: "Amazon",
  kohls: "Kohl's",
  woot: "Woot",
  newegg: "Newegg",
  gamestop: "GameStop",
  bjs: "BJ's Wholesale",
  meijer: "Meijer",
  menards: "Menards",
  academy: "Academy Sports",
  walgreens: "Walgreens",
  zappos: "Zappos",
  jcpenney: "JCPenney",
  belk: "Belk",
  michaels: "Michaels",
  nike: "Nike",
  adidas: "Adidas",
  reebok: "Reebok",
  skechers: "Skechers",
  puma: "Puma",
  underarmour: "Under Armour",
  columbia: "Columbia",
  nordstromrack: "Nordstrom Rack",
  aldo: "Aldo",
  vitacost: "Vitacost",
  journeys: "Journeys",
  shopdisney: "shopDisney",
  ninjakitchen: "Ninja Kitchen",
  hottopic: "Hot Topic",
  officedepot: "Office Depot",
  barnesnoble: "Barnes & Noble",
  razer: "Razer",
  pacsun: "PacSun",
  bathbodyworks: "Bath & Body Works",
  staples: "Staples",
  petsmart: "PetSmart",
  ulta: "Ulta Beauty",
  forever21: "Forever 21",
  gap: "Gap",
  petco: "Petco",
  americaneagle: "American Eagle",
  aerie: "Aerie",
  carters: "Carter's",
  abercrombie: "Abercrombie & Fitch",
  hollister: "Hollister",
  oldnavy: "Old Navy",
  bananarepublic: "Banana Republic",
  stevemadden: "Steve Madden",
  athleta: "Athleta",
  gapfactory: "Gap Factory",
  bananarepublicfactory: "Banana Republic Factory",
  boxlunch: "BoxLunch",
  verabradley: "Vera Bradley",
  footlocker: "Foot Locker",
  champs: "Champs Sports",
};

// Online-only retailers have no stores to check nearby.
const ONLINE_ONLY_RETAILERS = new Set<string>(ONLINE_ONLY_RETAILER_IDS);

// Retailers with a store catalog we can resolve full store labels from.
const RETAILERS_WITH_STORES = new Set(["walmart", "home_depot", "dollar_general", "target", "bestbuy", "costco", "lowes", "tractor_supply", "northern_tool", "samsclub"]);

const itemKey = (fav: GlobalFavorite) => `${fav.retailer}#${fav.upc}`;

const cleanStoreId = (storeId?: string) =>
  (storeId || "").replace("-PENNY-ITEM", "").replace("-CRAZY-DEALS", "");

// The result to refresh the favorite from: its own store when present in the
// results, otherwise the cheapest in-stock store.
const pickResultForFavorite = (
  fav: GlobalFavorite,
  results: Array<{ error?: string; status?: string; inventory?: number; storeId?: string; storePrice: number; onlinePrice?: number }>
) => {
  const inStock = results.filter(
    (r) => !r.error && (r.status === "IN_STOCK" || (r.inventory ?? 0) > 0)
  );
  if (!inStock.length) return null;
  const own = fav.storeId
    ? inStock.find((r) => cleanStoreId(r.storeId) === cleanStoreId(fav.storeId))
    : null;
  return own || inStock.reduce((a, b) => (b.storePrice < a.storePrice ? b : a));
};

export default function GlobalFavoritesButton({ iconSize = 24, variant = "light" }: GlobalFavoritesButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [isClearingStale, setIsClearingStale] = useState(false);
  const [retailerFilter, setRetailerFilter] = useState("all");
  const [menuKey, setMenuKey] = useState<string | null>(null);
  const [openedAt, setOpenedAt] = useState(0);
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const isFreeUser = isFreeTier(user);

  // Nearby search state — same in-place flow as ProductItem/LeadsPage.
  const [showNearbyModal, setShowNearbyModal] = useState(false);
  const [isSearchingNearby, setIsSearchingNearby] = useState(false);
  const [nearbySearchError, setNearbySearchError] = useState<string | null>(null);
  const [nearbySearchResults, setNearbySearchResults] = useState<StoreProductResult[]>([]);
  const [nearbyHomeDepotResults, setNearbyHomeDepotResults] = useState<HomeDepotStoreProductResult[]>([]);
  const [nearbyAmazonData, setNearbyAmazonData] = useState<{
    price?: number; title?: string; url?: string; isPrime?: boolean;
  } | null>(null);
  const [nearbyRetailer, setNearbyRetailer] = useState<string>("walmart");
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [pendingNearbyFav, setPendingNearbyFav] = useState<GlobalFavorite | null>(null);

  const { data: favorites = [], isLoading } = useQuery({
    queryKey: globalFavoritesQueryKey,
    queryFn: getAllFavorites,
    enabled: isOpen && !isFreeUser,
    staleTime: 60 * 1000,
  });
  const experience = getGlobalFavoritesExperience(isFreeUser, favorites.length);

  // Store catalogs per retailer, for resolving full store labels ("Miami, FL - #1916").
  const [storeCatalogs, setStoreCatalogs] = useState<Record<string, StoreData>>({});

  useEffect(() => {
    if (!isOpen || !experience.canUseStoreFeatures) return;
    const needed = [...new Set(favorites.map((f) => f.retailer))].filter(
      (r) => RETAILERS_WITH_STORES.has(r) && !storeCatalogs[r]
    );
    needed.forEach((r) => {
      storesLoader
        .loadStores(r as RetailerWithStores)
        .then((stores) => {
          setStoreCatalogs((prev) => (prev[r] ? prev : { ...prev, [r]: stores }));
        })
        .catch(() => {});
    });
  }, [isOpen, favorites, storeCatalogs, experience.canUseStoreFeatures]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleRemove = async (fav: GlobalFavorite) => {
    if (removingKey) return;
    setRemovingKey(itemKey(fav));
    try {
      await removeFavoriteWithRetailer(fav.upc, fav.retailer, true);
      queryClient.setQueryData<GlobalFavorite[]>(globalFavoritesQueryKey, (old) =>
        (old || []).filter((f) => itemKey(f) !== itemKey(fav))
      );
      queryClient.invalidateQueries({
        queryKey: ["favorites", "list"],
        predicate: (query) => query.queryKey[2] !== "global-all",
      });
      toast.success("Removed from saved");
    } catch {
      toast.error("Failed to remove from saved");
    } finally {
      setRemovingKey(null);
    }
  };

  // Retailer tabs, then Recent vs "Prices may have changed" (older than PRICE_OUTDATED_AFTER_MS).
  const retailerTabs = [...favorites.reduce((counts, fav) => counts.set(fav.retailer, (counts.get(fav.retailer) ?? 0) + 1), new Map<string, number>())]
    .sort((a, b) => b[1] - a[1]);
  const activeFilter = retailerFilter !== "all" && retailerTabs.some(([r]) => r === retailerFilter) ? retailerFilter : "all";
  const isStale = (fav: GlobalFavorite) => {
    const freshAt = Math.max(fav.added || 0, fav.lastChecked || 0);
    return freshAt > 0 && openedAt - freshAt > PRICE_OUTDATED_AFTER_MS;
  };
  const visibleFavorites = activeFilter === "all" ? favorites : favorites.filter((fav) => fav.retailer === activeFilter);
  const staleFavorites = visibleFavorites.filter(isStale);
  const freshFavorites = visibleFavorites.filter((fav) => !isStale(fav));

  const handleClearStale = async () => {
    if (isClearingStale || staleFavorites.length === 0) return;
    const targets = staleFavorites;
    if (!window.confirm(`Remove ${targets.length} older ${targets.length === 1 ? "save" : "saves"}? Their prices may have changed since you saved them.`)) return;
    setIsClearingStale(true);
    const results = await Promise.allSettled(targets.map((fav) => removeFavoriteWithRetailer(fav.upc, fav.retailer, true)));
    const removed = new Set(targets.filter((_, i) => results[i].status === "fulfilled").map(itemKey));
    queryClient.setQueryData<GlobalFavorite[]>(globalFavoritesQueryKey, (old) => (old || []).filter((f) => !removed.has(itemKey(f))));
    queryClient.invalidateQueries({
      queryKey: ["favorites", "list"],
      predicate: (query) => query.queryKey[2] !== "global-all",
    });
    if (removed.size < targets.length) {
      toast.error("Some saves couldn't be removed");
      queryClient.invalidateQueries({ queryKey: globalFavoritesQueryKey });
    } else {
      toast.success(`${removed.size} older ${removed.size === 1 ? "save" : "saves"} cleared`);
    }
    setIsClearingStale(false);
  };

  const openSheet = () => {
    setOpenedAt(Date.now());
    setMenuKey(null);
    setIsOpen(true);
  };

  const handleClearAll = async () => {
    if (!experience.canClearAll || isClearingAll || favorites.length === 0) return;
    if (!window.confirm("Remove all favorites across every retailer? This can't be undone.")) return;
    setIsClearingAll(true);
    try {
      await clearAllFavorites();
      queryClient.setQueryData<GlobalFavorite[]>(globalFavoritesQueryKey, []);
      queryClient.invalidateQueries({
        queryKey: ["favorites", "list"],
        predicate: (query) => query.queryKey[2] !== "global-all",
      });
      toast.success("All favorites cleared");
    } catch {
      toast.error("Failed to clear favorites");
    } finally {
      setIsClearingAll(false);
    }
  };

  // Write the freshest price/stock back onto the favorite so it doesn't need
  // another check next time.
  const applySnapshotUpdate = useCallback(
    async (
      fav: GlobalFavorite,
      result: { storePrice: number; inventory?: number; onlinePrice?: number }
    ) => {
      if (!result.storePrice || result.storePrice <= 0) return;
      try {
        await updateFavoriteSnapshot(fav.upc, fav.retailer, {
          storePrice: result.storePrice,
          onlinePrice: result.onlinePrice && result.onlinePrice > 0 ? result.onlinePrice : undefined,
          stock: result.inventory ?? 0,
        });
        queryClient.setQueryData<GlobalFavorite[]>(globalFavoritesQueryKey, (old) =>
          (old || []).map((f) =>
            itemKey(f) === itemKey(fav)
              ? {
                  ...f,
                  savedPrice: result.storePrice,
                  savedOnlinePrice:
                    result.onlinePrice && result.onlinePrice > 0
                      ? result.onlinePrice
                      : f.savedOnlinePrice,
                  savedStock: result.inventory ?? 0,
                  lastChecked: Date.now(),
                }
              : f
          )
        );
      } catch {
        // Non-fatal: the nearby results still showed; the snapshot just stays as-is.
      }
    },
    [queryClient]
  );

  const performNearbySearch = useCallback(async (fav: GlobalFavorite, zipCode: string) => {
    const { searchCode, searchType } = getSearchParams({
      retailer: fav.retailer,
      productSku: fav.sku || fav.upc,
      productUpc: fav.upc || undefined,
    });
    const useSkuFallback =
      shouldFallbackToSku({ retailer: fav.retailer, productUpc: fav.upc, productSku: fav.sku }) && fav.sku;

    setNearbyRetailer(fav.retailer);
    setShowNearbyModal(true);
    setIsSearchingNearby(true);
    setNearbySearchError(null);
    setNearbySearchResults([]);
    setNearbyHomeDepotResults([]);
    setNearbyAmazonData(null);

    try {
      if (fav.retailer === "home_depot") {
        const response = await searchHomeDepotProduct(searchCode.trim(), zipCode);
        setNearbyHomeDepotResults(response.results);
        if (response.amazonPrice != null && response.amazonPrice > 0) {
          setNearbyAmazonData({
            price: response.amazonPrice,
            title: response.amazonTitle,
            url: response.amazonURL,
            isPrime: response.amazonIsPrime,
          });
        }
        if (response.results.length === 0) {
          setNearbySearchError("No results found for this product");
        }
        const match = pickResultForFavorite(fav, response.results);
        if (match) void applySnapshotUpdate(fav, match);
        return;
      }

      let response;
      try {
        response = await searchProduct(searchCode.trim(), searchType, zipCode, fav.retailer as RetailerType);
      } catch (error) {
        if (!useSkuFallback) throw error;
        response = await searchProduct(fav.sku!.trim(), "SKU", zipCode, fav.retailer as RetailerType);
      }
      setNearbySearchResults(response.results);
      if (response.amazonPrice != null && response.amazonPrice > 0) {
        setNearbyAmazonData({
          price: response.amazonPrice,
          title: response.amazonTitle,
          url: response.amazonURL,
          isPrime: response.amazonIsPrime,
        });
      }
      if (response.results.length === 0) {
        setNearbySearchError("No results found for this product");
      }
      const match = pickResultForFavorite(fav, response.results);
      if (match) void applySnapshotUpdate(fav, match);
    } catch (error) {
      setNearbySearchError(error instanceof Error ? error.message : "Failed to search for product");
    } finally {
      setIsSearchingNearby(false);
    }
  }, [applySnapshotUpdate]);

  const handleCheckNearby = (fav: GlobalFavorite) => {
    const zipCode = user?.zipCode?.trim();
    if (!zipCode) {
      // ZipCodeModal stacks below this modal, so close it first.
      setPendingNearbyFav(fav);
      setIsOpen(false);
      setShowZipCodeModal(true);
      return;
    }
    void performNearbySearch(fav, zipCode);
  };

  const closeNearbyModal = () => {
    setShowNearbyModal(false);
    setNearbySearchResults([]);
    setNearbyHomeDepotResults([]);
    setNearbySearchError(null);
    setNearbyAmazonData(null);
  };

  const iconColor = variant === "light" ? "text-white/80 hover:text-white" : "text-gray-600 hover:text-gray-900";

  const renderFavorite = (fav: GlobalFavorite, stale: boolean) => {
    const isRemoving = removingKey === itemKey(fav);
    // Prefer whichever price is fresher: the product DB or the
    // snapshot refreshed by the last nearby check.
    const livePrice = fav.storePrice?.length ? fav.storePrice[fav.storePrice.length - 1] : 0;
    const snapshotFresher = (fav.lastChecked || 0) > (fav.lastUpdated || 0);
    const storePrice = snapshotFresher && fav.savedPrice ? fav.savedPrice : livePrice || fav.savedPrice || 0;
    const onlinePrice = fav.onlinePrice || fav.savedOnlinePrice || 0;
    const discountPercent = storePrice > 0 && onlinePrice > storePrice
      ? Math.round(((onlinePrice - storePrice) / onlinePrice) * 100)
      : 0;
    const supportsNearby = experience.canUseStoreFeatures &&
      !ONLINE_ONLY_RETAILERS.has(fav.retailer) && fav.storeId !== "online";
    const storeLabel = !experience.canUseStoreFeatures || ONLINE_ONLY_RETAILERS.has(fav.retailer)
      ? null
      : resolveStoreDisplayLabel(fav.retailer, fav.storeId, storeCatalogs[fav.retailer]);
    const retailerUrl = resolveFavoriteRetailerUrl({
      candidateUrl: fav.url,
      retailer: fav.retailer,
      productId: fav.sku || fav.upc,
    });
    const stockText = fav.inStock === false ? "Out of stock" : (fav.savedStock ?? 0) > 0 ? `${fav.savedStock} in stock` : null;
    const meta = [RETAILER_LABELS[fav.retailer] || fav.retailer, stockText, storeLabel].filter(Boolean).join(" · ");
    const freshAt = Math.max(fav.added || 0, fav.lastChecked || 0);
    const key = itemKey(fav);
    const pill = "inline-flex h-7 items-center gap-1 rounded-full bg-[#f1eafe] px-2.5 text-xs font-semibold text-[#6d28d9] transition hover:bg-[#e9dffd] disabled:opacity-60 dark:bg-[#7c3aed3d] dark:text-[#c4b5fd] dark:hover:bg-[#7c3aed52]";
    return (
      <div key={key} className={`flex items-center gap-3 px-4 py-2.5 ${isRemoving ? "opacity-50" : ""}`}>
        {fav.image ? (
          <img src={getProductImageUrl(fav.image, "150")} alt="" className="h-14 w-14 flex-shrink-0 rounded-xl border border-gray-100 bg-white object-contain dark:border-transparent" />
        ) : (
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-dark-700">
            <Package className="h-5 w-5 text-gray-500 dark:text-dark-300" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-medium text-gray-900 dark:text-white" title={fav.name || fav.upc}>{fav.name || fav.upc}</p>
          <p className={`mt-0.5 truncate text-xs ${fav.inStock === false ? "text-red-700 dark:text-red-300" : "text-gray-600 dark:text-dark-200"}`}>{meta}</p>
          <div className="mt-0.5 flex items-baseline gap-1.5 tabular-nums">
            {storePrice > 0 && <span className={`text-base font-extrabold tracking-tight ${stale ? "text-gray-500 dark:text-dark-300" : "text-gray-900 dark:text-white"}`}>{formatPrice(storePrice)}</span>}
            {onlinePrice > storePrice && <span className="text-xs text-gray-600 line-through dark:text-dark-300">{formatPrice(onlinePrice)}</span>}
            {discountPercent > 0 && !stale && <span className="text-[11.5px] font-bold text-red-700 dark:text-red-300">-{discountPercent}%</span>}
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setMenuKey(menuKey === key ? null : key); }}
              disabled={isRemoving}
              aria-label={`More actions for ${fav.name || "saved deal"}`}
              aria-expanded={menuKey === key}
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-700 transition hover:bg-gray-100 dark:text-dark-100 dark:hover:bg-dark-700"
            >
              {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-[18px] w-[18px]" />}
            </button>
            {menuKey === key && (
              <div onClick={(e) => e.stopPropagation()} className="absolute right-0 top-8 z-10 w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-dark-600 dark:bg-dark-800">
                {freshAt > 0 && <p className="px-3 pb-1 pt-1.5 text-[11px] text-gray-500 dark:text-dark-300">{fav.lastChecked ? "Checked" : "Saved"} {formatRelativeTime(freshAt)}</p>}
                {retailerUrl && (
                  <a href={retailerUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuKey(null)} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 dark:text-dark-100 dark:hover:bg-dark-700">
                    <ExternalLink className="h-4 w-4" />View deal
                  </a>
                )}
                <button onClick={() => { setMenuKey(null); void handleRemove(fav); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20">
                  <Trash2 className="h-4 w-4" />Remove
                </button>
              </div>
            )}
          </div>
          {supportsNearby ? (
            <button onClick={() => handleCheckNearby(fav)} disabled={isSearchingNearby} className={pill}>
              {isSearchingNearby ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />}Check stock
            </button>
          ) : retailerUrl ? (
            <a href={retailerUrl} target="_blank" rel="noopener noreferrer" className={pill}>
              <ExternalLink className="h-3.5 w-3.5" />View
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  const renderModal = () =>
    isOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[10001] flex items-end md:items-center md:justify-center bg-black/50 sheet-backdrop-in"
        onClick={() => setIsOpen(false)}
      >
        <div
          className="bg-white dark:bg-dark-800 w-full md:w-[480px] md:max-w-[90vw] h-[75vh] md:h-auto md:max-h-[80vh] rounded-t-2xl md:rounded-2xl flex flex-col overflow-hidden shadow-2xl sheet-panel-in-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 px-4 pt-3 pb-2">
            <div className="mx-auto mb-2 h-[5px] w-[38px] rounded-full bg-gray-300 dark:bg-dark-600 md:hidden" />
            <div className="flex items-center gap-2">
              <h2 className="text-[19px] font-extrabold tracking-tight text-gray-900 dark:text-white">{experience.title}</h2>
              {favorites.length > 0 && (
                <span className="rounded-full bg-[#f1eafe] px-2 py-0.5 text-xs font-bold text-[#6d28d9] dark:bg-[#7c3aed3d] dark:text-[#c4b5fd]">
                  {experience.countLabel}
                </span>
              )}
              <div className="flex-1" />
              {experience.canClearAll && favorites.length > 0 && (
                <button
                  onClick={handleClearAll}
                  disabled={isClearingAll}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-gray-600 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-60 dark:text-dark-200 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  {isClearingAll && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Clear all
                </button>
              )}
              <button onClick={() => setIsOpen(false)} aria-label="Close saved deals" className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700 transition hover:bg-gray-200 dark:bg-dark-700 dark:text-dark-100 dark:hover:bg-dark-600">
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
            {retailerTabs.length > 1 && (
              <div className="-mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[["all", favorites.length] as const, ...retailerTabs].map(([id, count]) => {
                  const on = activeFilter === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setRetailerFilter(id)}
                      aria-pressed={on}
                      className={`inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition ${on ? "border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900" : "border-gray-200 text-gray-800 hover:bg-gray-50 dark:border-dark-600 dark:text-dark-100 dark:hover:bg-dark-700"}`}
                    >
                      {id === "all" ? "All" : RETAILER_LABELS[id] || id}
                      <span className={`text-[11px] ${on ? "opacity-70" : "text-gray-500 dark:text-dark-300"}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto pb-3" onClick={() => setMenuKey(null)}>
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-dark-400" />
              </div>
            ) : favorites.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center mb-4">
                  <Heart className="w-8 h-8 text-gray-400 dark:text-dark-400" />
                </div>
                <p className="text-gray-700 dark:text-dark-200 text-sm font-medium">{experience.emptyTitle}</p>
                <p className="text-gray-600 dark:text-dark-300 text-xs mt-1 max-w-[240px]">
                  {experience.emptyDescription}
                </p>
              </div>
            ) : (
              <>
                {freshFavorites.length > 0 && (
                  <div className="px-4 pb-1 pt-3 text-[12.5px] font-bold text-gray-700 dark:text-dark-200">Recent · {freshFavorites.length}</div>
                )}
                {freshFavorites.map((fav) => renderFavorite(fav, false))}
                {staleFavorites.length > 0 && (
                  <div className="flex items-center justify-between px-4 pb-1 pt-3.5">
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-amber-700 dark:text-amber-300">
                      <Clock className="h-3.5 w-3.5" />Prices may have changed · {staleFavorites.length}
                    </span>
                    <button onClick={() => void handleClearStale()} disabled={isClearingStale} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6d28d9] hover:underline disabled:opacity-60 dark:text-[#c4b5fd]">
                      {isClearingStale && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Clear these
                    </button>
                  </div>
                )}
                {staleFavorites.map((fav) => renderFavorite(fav, true))}
              </>
            )}
          </div>

          <div className="h-[env(safe-area-inset-bottom)] md:hidden flex-shrink-0" />
        </div>
      </div>,
      document.body
    );

  const sharedModals = (
    <>
      {showNearbyModal && (
        <NearbyResults
          isSearching={isSearchingNearby}
          searchError={nearbySearchError}
          onClose={closeNearbyModal}
          searchResults={nearbySearchResults}
          homeDepotSearchResults={nearbyHomeDepotResults}
          isHomeDepot={nearbyRetailer === "home_depot"}
          retailer={nearbyRetailer}
          amazonData={nearbyAmazonData || undefined}
        />
      )}
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => {
          setShowZipCodeModal(false);
          setPendingNearbyFav(null);
        }}
        onSuccess={async (zipCode) => {
          setShowZipCodeModal(false);
          if (pendingNearbyFav) {
            const fav = pendingNearbyFav;
            setPendingNearbyFav(null);
            await performNearbySearch(fav, zipCode);
          }
        }}
        currentZipCode={user?.zipCode || ""}
      />
    </>
  );

  if (isFreeUser) return null;

  if (variant === "sidebar") {
    return (
      <>
        <button
          onClick={openSheet}
          className="group relative flex items-center gap-3 w-full px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 text-primary-200 hover:text-white hover:bg-primary-800/40 hover:shadow-lg hover:shadow-primary-900/20"
        >
          <div className="transition-all duration-300 group-hover:text-primary-300 group-hover:scale-110">
            <Heart size={iconSize} />
          </div>
          <span className="transition-all duration-300 group-hover:translate-x-0.5 text-sm">{experience.title}</span>
        </button>
        {renderModal()}
        {sharedModals}
      </>
    );
  }

  return (
    <>
      <button onClick={openSheet} className={`relative p-2 rounded-xl transition-all ${iconColor} hover:bg-white/10`} aria-label={experience.triggerLabel}>
        <Heart size={iconSize} />
      </button>
      {renderModal()}
      {sharedModals}
    </>
  );
}
